import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    Breadcrumbs,
    Card,
    Chip,
    Divider,
    Grid,
    IconButton,
    Table,
    Typography,
    Input,
} from '@mui/joy';
import { ChevronRight, Visibility } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { CLayout } from '../../components';
import { get } from '../../api/api';
import { Pagination } from '@mui/material';
import './style.scss';
import {jwtDecode} from "jwt-decode";

interface User {
    id: number;
    name: string;
    email: string;
    roles: string[];
    subscription_plans: string[]; // ex: ["/subscription_plans/1", ...]
}

interface SubscriptionPlan {
    id: number;
    name: string;
}

export const VListUsers: React.FC = () => {
    const { t } = useTranslation();

    const [users, setUsers] = useState<User[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
    const [emailFilter, setEmailFilter] = useState<string>('');

    // Récupère les utilisateurs en appliquant les filtres via l'API
    const fetchUsers = async () => {
        try {
            const params = new URLSearchParams();
            params.append('page', page.toString());
            if(emailFilter)
            {
                params.append('email', emailFilter);
            }

            if (selectedPlan) {
                params.append('subscription_plans.id', selectedPlan.id.toString());
            }
            const response = await get({ url: `users?${params.toString()}` });
            // Lecture de l'en-tête Content-Range (exemple : "0-20/100")
            const contentRange =
                response.headers['content-range'] || response.headers.get('Content-Range');
            if (contentRange) {
                const totalItems = parseInt(contentRange.split('/')[1], 10);
                setTotalPages(Math.ceil(totalItems / 20));
            }
            setUsers(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des utilisateurs :', error);
        }
    };

    // Récupère les plans d’abonnement pour le filtre
    const fetchPlans = async () => {
        try {
            const res = await get({ url: 'subscription_plans' });
            setPlans(res.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des plans d’abonnement :', error);
        }
    };

    useEffect(() => {
        document.title = `EevenPro - ${t('Liste des utilisateurs')}`;
    }, [t]);

    useEffect(() => {
        // Vérifie que l'utilisateur a le rôle ADMIN
        const token = localStorage.getItem('token') || '';
        const decode: any = jwtDecode(token);
        if (!decode.roles.includes('ROLE_ADMIN')) {
            window.location.href = '/';
        }
    }, []);

    // Recharger les utilisateurs dès qu'un filtre change
    useEffect(() => {
        fetchUsers();
    }, [page, emailFilter, selectedPlan]);

    useEffect(() => {
        fetchPlans();
    }, []);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <CLayout>
            <section id="VListUsers">
                <Card className="rounded-none my-4 drop-shadow-lg mt-4" variant="outlined">
                    <Grid container spacing={2} className="items-center">
                        <Grid xs={4}>
                            <Breadcrumbs separator={<ChevronRight fontSize="small" />}>
                                <Link to="/">{t('Eeven PRO')}</Link>
                                <Typography component="span" level="body-md">
                                    {t('Liste des utilisateurs')}
                                </Typography>
                            </Breadcrumbs>
                        </Grid>

                        <Grid xs={6}>
                            <Grid container spacing={1}>
                                <Grid xs={6}>
                                    <Input
                                        placeholder={t('Filtrer par email')}
                                        value={emailFilter}
                                        onChange={(e) => setEmailFilter(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid xs={6}>
                                    <Autocomplete
                                        placeholder={t("Plan d'abonnement")}
                                        options={plans}
                                        getOptionLabel={(option) => option.name || ''}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={(_e, newValue) => setSelectedPlan(newValue)}
                                        value={selectedPlan}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Pagination
                                count={totalPages}
                                shape="rounded"
                                onChange={handlePageChange}
                                page={page}
                            />
                        </Grid>
                    </Grid>
                </Card>

                <Grid container spacing={2} className="justify-between items-center">
                    <Grid xs={4}>
                        <Typography component="h2" level="h4" className="!mt-2 !text-3xl !text-black">
                            {t('Utilisateurs')}
                        </Typography>
                        <Divider className="!w-[60%] !bg-black !h-[2px] !mt-1 !mb-4" />
                    </Grid>
                </Grid>

                <Table>
                    <thead>
                    <tr>
                        <th>{t('Nom')}</th>
                        <th>{t('Email')}</th>
                        <th>{t('Roles')}</th>
                        <th>{t('Actions')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.map((user) => (
                        <tr key={user.id}>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>
                                {user.roles.map((role, idx) =>
                                    idx < 2 ? (
                                        <Chip
                                            key={idx}
                                            variant="solid"
                                            color={role === 'ROLE_ADMIN' ? 'success' : 'primary'}
                                            className="mr-4"
                                        >
                                            {role}
                                        </Chip>
                                    ) : null
                                )}
                            </td>
                            <td>
                                <IconButton
                                    onClick={() => {
                                        window.location.href = `/users/show?id=${user.id}`;
                                    }}
                                >
                                    <Visibility />
                                </IconButton>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </section>
        </CLayout>
    );
};